import {
  CaretRightOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudSyncOutlined,
  CloudUploadOutlined, LaptopOutlined,
  PauseOutlined,
  YoutubeOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App,
  Button, Progress, Space, Tag, Typography,
} from 'antd';
import { truncate } from 'lodash-es';
import {
  useCallback, useContext,
} from 'react';
import { useDropzone } from 'react-dropzone';
import { UploadContext } from '/src/constants/contexts';
import { useTheme } from '/src/contexts/ThemeContext';
import useIsDesktop from '/src/hooks/useIsDesktop';

function UploadBox() {
  const {
    file,
    video,
    clearFile,
    onDrop,
    isUploading,
    isUploaded,
    progress,
    pauseUpload,
    resumeUpload,
    initialized,
    isActiveInAnotherTab,
  } = useContext(UploadContext);
  const { modal } = App.useApp();
  const isDesktop = useIsDesktop();
  const { isDark } = useTheme();
  const disabled = false;
  // const hasPartialUpload = video?.s3?.parts && Object.keys(video.s3.parts).length > 0 &&
  //           currentProgress?.percent > 0 && currentProgress?.percent < 100;

  const formatPercent = (percent) => {
    const size = (((percent / 100) * video.size) / (1024 * 1024)).toFixed(1);
    const text = percent < 10 ? `${size}Mb` : `${size}Mb / ${percent.toFixed(0)}%`;
    return <span className="num text-xs">{text}</span>;
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled,
    accept: {
      'video/*': [],
      'audio/*': [],
    },
  });
  // console.log({
  //   isActiveInAnotherTab,
  //   progress,
  //   uploadingVideo,
  //   video,
  //   initialized,
  // });

  const handleCancel = useCallback(() => {
    modal.confirm({
      icon: <CloudUploadOutlined />,
      title: 'لغو آپلود',
      content: 'از توقف فرآیند آپلود اطمینان دارید؟',
      onOk() {
        clearFile();
      },
      okText: 'بله، متوقف شود',
      cancelText: 'خیر',
    });
  }, [video?.id, progress]);

  function renderContent() {
    if (isUploaded) {
      const isPublished = video.state === 'PUBLISHED';
      return (
        <div className="flex items-center gap-3 rounded-xl bg-subtle px-4 py-3">
          <CheckCircleOutlined className="text-lg text-success" />
          <div className="min-w-0">
            <Typography.Text strong>آپلود فایل تکمیل شد</Typography.Text>
            <Typography.Paragraph
              className="mb-0 mt-1 text-xs"
              ellipsis={{ tooltip: video.filename || video.title || video.s3?.key }}
              style={{ direction: 'ltr' }}
            >
              {video.filename || video.title || video.s3?.key}
            </Typography.Paragraph>
            {isPublished && (
              <Space size={4} wrap className="mt-2">
                <Tag bordered={false} color="success">منتشر شده</Tag>
                {video.url && (
                  <Button
                    type="link"
                    size="small"
                    icon={<YoutubeOutlined />}
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-1"
                  >
                    مشاهده ویدیو
                  </Button>
                )}
              </Space>
            )}
          </div>
        </div>
      );
    }
    if (file || isUploading || initialized || isUploaded) {
      return (
        <div className="w-full">
          <div className="w-full gap-0 flex p-1 relative items-center">
            <Progress
              percent={isUploaded ? 100 : progress}
              percentPosition={{ align: 'end', type: 'inner' }}
              format={formatPercent}
              strokeColor={isDark ? 'crimson' : '#535353'}
              size={['100%', 20]}
              style={{ flex: 1 }}
            />
            {/* {
              file && (
                <code
                  className="absolute"
                  style={{
                    left: 50,
                    background: 'transparent',
                    fontSize: 12,
                    bottom: 13,
                    color: 'gray',
                  }}
                >
                  {(((progress / 100) * video.size) / (1024 * 1024)).toFixed(1)}
                  Mb
                </code>
              ) */}
            {/* } */}

          </div>

          <div className="flex justify-between">
            <div className="flex gap-2">
              {file && !isUploaded && (
                <>
                  {isUploading ? (
                    <Button
                      size="small"
                      onClick={pauseUpload}
                      icon={<PauseOutlined />}
                      disabled={disabled}
                      // className="hidden md:inline-flex"
                    >
                      <span className="hidden md:inline">توقف موقت</span>
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      onClick={resumeUpload}
                      icon={<CaretRightOutlined />}
                      type="primary"
                      disabled={disabled}
                    >
                      ادامه آپلود
                    </Button>
                  )}
                  <Button
                    size="small"
                    onClick={handleCancel}
                    icon={<CloseCircleOutlined />}
                    danger
                    disabled={disabled}
                  >
                    لغو آپلود
                  </Button>
                </>
              )}

            </div>
            <div className="flex gap-2">
              <Typography.Paragraph style={{ direction: 'ltr' }}>
                {truncate(video.filename || video.title || video.s3?.key, { length: isDesktop ? 60 : 40 })}
              </Typography.Paragraph>
            </div>
          </div>
          {
            isActiveInAnotherTab && (
              <div>
                <Alert
                  icon={<CloudSyncOutlined />}
                  message="در حال آپلود"
                  description="این فایل در تب دیگری در حال آپلود است"
                  showIcon
                />
              </div>
            )
          }

          {!isUploaded && !isActiveInAnotherTab && !file && (
            <div>
              <Alert
                message={`آپلود ناتمام (${Math.floor(progress)}% انجام شده)`}
                description={(
                  <div>
                    برای ادامه آپلود فایل &quot;
                    {video.filename}
                    &quot; را انتخاب کنید.
                    <div {...getRootProps()} className="mt-2">
                      <input {...getInputProps()} />
                      <Button
                        type="primary"
                        icon={<LaptopOutlined />}
                        size="small"
                        disabled={disabled}
                      >
                        انتخاب فایل
                      </Button>
                    </div>
                  </div>
                )}
                type="info"
                showIcon
              />
            </div>
          )}
        </div>
      );
    }
    return null;
  }

  function renderUploadBox() {
    if (!video || file || isUploaded || isUploading || initialized) {
      return null;
    }

    return (
      <div
        {...getRootProps({
          className: [
            'group cursor-pointer rounded-card-lg border border-hairline bg-subtle px-4 py-8 text-center transition-all duration-200 md:px-6 md:py-10',
            isDragActive ? 'border-brand bg-brand-50' : 'hover:border-brand/40 hover:bg-elevated',
          ].join(' '),
        })}
      >
        <input {...getInputProps()} />
        <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl bg-elevated text-fg-tertiary shadow-sm transition-colors group-hover:text-brand">
          <CloudUploadOutlined className="text-3xl" />
        </div>
        <Typography.Title level={4} className="mb-2">
          {isDragActive ? 'فایل را رها کنید' : 'آپلود فایل ویدیو'}
        </Typography.Title>
        <Typography.Paragraph className="mx-auto mb-5 max-w-xl text-fg-secondary">
          {isDesktop
            ? 'فایل را بکشید و اینجا رها کنید، یا با دکمه زیر از سیستم انتخاب کنید.'
            : 'برای انتخاب ویدیو از دستگاه، دکمه زیر را بزنید.'}
        </Typography.Paragraph>
        <Button
          type="primary"
          icon={<LaptopOutlined />}
          size="large"
          disabled={disabled}
        >
          انتخاب فایل
        </Button>
      </div>
    );
  }

  if (video?.fileState === 'REMOVED') {
    return null;
  }

  return (
    <>
      {renderUploadBox()}
      {renderContent()}
    </>
  );
}
export default UploadBox;
